// src/contexts/SocialContext.js
import React, { createContext, useState, useCallback } from 'react';

/**
 * SocialContext — governs the backend-controlled social feature kill switch
 * and the current user's social stats (follower count, pending requests).
 *
 * Intentionally kept separate from OfflineModeContext: a backend kill switch
 * disabling social features is NOT the same as the device being offline.
 */
export const SocialContext = createContext({
  socialFeaturesEnabled: false,
  setSocialFeaturesEnabled: () => {},
  disableSocialFeatures: () => {},
  followerCount: 0,
  pendingRequests: 0,
  setSocialStats: () => {},
});

export const SocialProvider = ({ children }) => {
  const [socialFeaturesEnabled, setSocialFeaturesEnabled] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [pendingRequests, setPendingRequests] = useState(0);

  /**
   * Called when a 503 is received on any social endpoint, or when the config
   * endpoint returns social_features_enabled: false.
   */
  const disableSocialFeatures = useCallback(() => {
    setSocialFeaturesEnabled(false);
  }, []);

  /**
   * Batch-update current user social stats from the connections response.
   * @param {{ follower_count?: number, pending_count?: number }} stats
   */
  const setSocialStats = useCallback(({ follower_count = 0, pending_count = 0 } = {}) => {
    setFollowerCount(follower_count);
    setPendingRequests(pending_count);
  }, []);

  return (
    <SocialContext.Provider
      value={{
        socialFeaturesEnabled,
        setSocialFeaturesEnabled,
        disableSocialFeatures,
        followerCount,
        pendingRequests,
        setSocialStats,
      }}
    >
      {children}
    </SocialContext.Provider>
  );
};
